import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { RouterStateSnapshot } from '@angular/router';
import { RouterState } from '@ngxs/router-plugin';
import { Select } from '@ngxs/store';
import { environment } from 'common/environments/environment';
import { BaseContentEntity } from 'common/models';
import { BrandService } from '../../../../common/services/brand.service';
import { DataService } from 'common/services/data.service';
import { MarketingProductService, MarketingSection } from 'public/app/services/marketing-product.service';
import Rellax, { RellaxInstance } from 'rellax';
import { Observable, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { SignupPanelComponent } from '../../../../common/components/signup-panel/signup-panel.component';
import { DataResolverService } from '../../../../common/content/services/data-resolver.service';
import { Alert } from 'common/components/alert/alert.component';

export interface HeroContentEntities {
    branding?: BaseContentEntity | null;
    title?: BaseContentEntity | null;
    search?: BaseContentEntity | null;
}

export interface FixedPageContentEntities {
    alerts?: Alert[] | null;
    browse?: BaseContentEntity | null;
    whatsnewStatic?: BaseContentEntity | null;
    quicklinks?: BaseContentEntity | null;
    whatsnewDynamic?: BaseContentEntity | null;
    whatsnew?: BaseContentEntity | null;
    notifications?: BaseContentEntity | null;
}

const findByTemplate = (entities: BaseContentEntity[], template: string): BaseContentEntity|undefined => entities.find((entity) => Boolean(entity?.ComponentTemplate && entity.ComponentTemplate.Title === template));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const removeFromContent = (pageContent: any, entity: BaseContentEntity) => {
    const entityIndex = pageContent.indexOf(entity);
    let newPageContent = [];
    if (entityIndex > -1) {
        pageContent.splice(entityIndex, 1);
        newPageContent = pageContent;
    }

    return newPageContent;
};

@Component({
    selector: 'utc-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class HomeComponent implements OnInit, OnDestroy {
    @Select(RouterState.state) routerState$: Observable<RouterStateSnapshot>;
    @ViewChild(SignupPanelComponent) signUpPanel: SignupPanelComponent;

    rellax!: RellaxInstance;
    featuredContent$: Observable<MarketingSection> = this.marketingProductService.getMarketingContent('HOMEPAGE.HIGHLIGHTS');
    showCovidInfo = environment.features.covid19Information;
    brand = environment.brand;
    showCBPBackgroup = false;
    logoutFrameUrl: string = environment.logoutFrameUrl;
    isLogoutRedirect = false;
    brandLogoAltText = 'brand logo '
    heroContent: HeroContentEntities;
    fixedPageContent: FixedPageContentEntities;

    content$: Observable<{ hero: BaseContentEntity[], page: BaseContentEntity[] }>;
    remainingPageContent$: Observable<BaseContentEntity[]>;

    public isICP = this.brandService.isICPBrand(environment.brand);
    public showRegulatoryQuestionnaire = environment.features.regulatoryQuestionnaire;
    public showGoSiteRegistration = this.isICP && environment.features.goSiteRegistration;
    searchBoxTemplate = this.isICP ? 'Search Box [Nav]-01' : 'Search Box [Nav]';

    private ngOnDestroy$ = new Subject();


    constructor(
        private readonly marketingProductService: MarketingProductService,
        private dataResolverService: DataResolverService,
        private readonly location: Location,
        private dataService:DataService,
        private readonly brandService: BrandService
    ) {}

    ngOnInit() {
        this.brandLogoAltText += this.brand;
        this.showCBPBackgroup = (this.brand === 'Carrier' || this.brand === 'Bryant');
        this.showCovidInfo = environment.features.covid19Information && this.showCBPBackgroup;
        this.routerState$.pipe(
            takeUntil(this.ngOnDestroy$)
        ).subscribe((routerState) => {
            this.isLogoutRedirect = routerState.url.includes('logout');
            this.rellax = new Rellax('.rellax');
            this.location.replaceState('/home');
        });

        this.content$ = this.dataResolverService.pageData$.pipe(
            map((data) => {
                const regions = data.Regions ? data.Regions : [];

                return ([] as BaseContentEntity[]).concat(...regions.map((region: {Entities: string}) => region.Entities));
            }),
            map((entities) => {
                const heroContent: BaseContentEntity[] = [];
                const pageContent: BaseContentEntity[] = [];

                entities.forEach((entity) => {
                    const template = entity?.ComponentTemplate && entity.ComponentTemplate.Title;

                    if (template === 'CT_Hero-Basic' || template === 'Search Box [Nav]' || template === 'Search Box [Nav]-01' || template === 'CT_Hero-Img') {
                        heroContent.push(entity);
                    }
                    else {
                        pageContent.push(entity);
                    }
                });

                return {
                    hero: heroContent,
                    page: pageContent
                };
            })
        );

        this.content$.subscribe(({ hero, page }) => {
            this.heroContent = {};
            this.fixedPageContent = {};

            this.heroContent = {
                branding: findByTemplate(hero, 'CT_Hero-Img'),
                title: findByTemplate(hero, 'CT_Hero-Basic'),
                search: findByTemplate(hero, this.searchBoxTemplate)
            };

            let parsedPage = page;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const alerts: any = findByTemplate(parsedPage, 'CT_Blks1-w100-06');

            if (alerts) {
                this.fixedPageContent.alerts = alerts;
                parsedPage = removeFromContent(parsedPage, alerts);
            }
            const browse = findByTemplate(page, 'CT_Blks1-w100-Animation');
            if (browse) {
                this.fixedPageContent.browse = browse;
                parsedPage = removeFromContent(parsedPage, browse);
            }
            const whatsnewStatic = findByTemplate(parsedPage, 'CT_CardCollection-w1-08');
            if (whatsnewStatic) {
                this.fixedPageContent.whatsnewStatic = whatsnewStatic;
                parsedPage = removeFromContent(parsedPage, whatsnewStatic);
            }
            const whatsnewDynamic = findByTemplate(parsedPage, 'CT_Blks1-w100-07');
            if (whatsnewDynamic) {
                this.fixedPageContent.whatsnewDynamic = whatsnewDynamic;
                parsedPage = removeFromContent(parsedPage, whatsnewDynamic);
            }

            const quicklinks = findByTemplate(parsedPage, 'CT_CardCollection-w3-01');
            if (quicklinks) {
                this.fixedPageContent.quicklinks = quicklinks;
                parsedPage = removeFromContent(parsedPage, quicklinks);
            }

            const notifications = findByTemplate(parsedPage, 'CT_Blks4-w100-News');
            if (notifications) {
                this.fixedPageContent.notifications = notifications;
                parsedPage = removeFromContent(parsedPage, notifications);
            }

            this.remainingPageContent$ = of(parsedPage);
        });

        this.dataService.signUpDataSubject.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.signUpPanel.focusForm();
        });
    }

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
